import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, MoreHorizontal } from "lucide-react";
import { Button } from "../Button";
import { DropdownMenu, MenuItem } from "../DropdownMenu";
import { Modal } from "../Modal";
import type { ListViewHeaderAction } from "./types";
import { useT } from "../../../app/lib/i18n";
import { getPortalContainer } from "../../lib/portalContainer";
import * as s from "./ListView.css";

const TOOLBAR_GAP_PX = 8
/** Keep a little free space so we collapse before flex wraps actions to a second row. */
const MIN_SPACER_PX = 8
/** Collapse earlier than a perfect fit — avoids the wrap-then-collapse flicker band. */
const COLLAPSE_EARLY_PX = 64

function ActionButton({ action }: { action: ListViewHeaderAction }) {
  const common = {
    type: "button" as const,
    size: "sm" as const,
    color: action.color ?? ("primary" as const),
    variant: action.variant ?? (action.iconOnly ? ("outline" as const) : ("solid" as const)),
    disabled: action.disabled,
    onClick: action.onClick,
    title: action.iconOnly ? action.label : undefined,
    "aria-label": action.iconOnly ? action.label : undefined,
  };

  if (action.iconOnly) {
    return (
      <Button
        {...common}
        className={s.listViewToolbarIconAction}
        iconLeft={action.icon}
      />
    );
  }

  return (
    <Button {...common} iconLeft={action.icon}>
      {action.label}
    </Button>
  );
}

/**
 * Trailing toolbar actions + optional fullscreen.
 * When `expand` is true, actions render as buttons until the toolbar is too
 * narrow — then they collapse into a ⋯ menu. Fullscreen stays outside the menu.
 * When `presentation` is `modal`, the ⋯ control always opens a modal.
 */
export function ListViewToolbarActions({
  actions,
  expand = false,
  fullscreen,
  presentation = "menu",
}: {
  actions: ListViewHeaderAction[];
  /** When true, prefer individual buttons (may collapse on overflow). */
  expand?: boolean;
  fullscreen?: {
    isFullscreen: boolean;
    onToggle: () => void;
  };
  /** Open overflow actions in a modal instead of a dropdown (narrow chrome). */
  presentation?: "menu" | "modal";
}) {
  const t = useT();
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const useModal = presentation === "modal";

  const checkOverflow = useCallback(() => {
    if (!expand || actions.length === 0 || useModal) {
      setOverflowing(false);
      return;
    }
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;

    const toolbar = root.closest("[data-listview-toolbar]");
    if (!(toolbar instanceof HTMLElement)) return;

    const needed = measure.scrollWidth;
    let other = 0;
    let otherCount = 0;
    const addOther = (width: number) => {
      other += width;
      otherCount += 1;
    };

    const accountForActionsRoot = (actionsEl: HTMLElement) => {
      const fs = actionsEl.querySelector("[data-fullscreen-btn]");
      if (fs instanceof HTMLElement) {
        addOther(fs.offsetWidth);
        // Gap between action buttons and fullscreen inside the cluster
        other += TOOLBAR_GAP_PX;
      }
    };

    for (const child of Array.from(toolbar.children)) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.dataset.actionsMeasure != null) continue;
      if (child.dataset.toolbarSpacer != null) continue;

      if (child === root) {
        accountForActionsRoot(child);
        continue;
      }
      if (child.contains(root)) {
        for (const tChild of Array.from(child.children)) {
          if (!(tChild instanceof HTMLElement)) continue;
          if (tChild === root || tChild.contains(root)) {
            accountForActionsRoot(root);
            continue;
          }
          addOther(tChild.offsetWidth);
        }
        continue;
      }
      addOther(child.offsetWidth);
    }

    const styles = getComputedStyle(toolbar);
    const padX =
      (parseFloat(styles.paddingLeft) || 0) +
      (parseFloat(styles.paddingRight) || 0);
    const contentWidth = toolbar.clientWidth - padX;

    // Gaps between fixed items, plus one gap before the actions cluster.
    const gaps = otherCount * TOOLBAR_GAP_PX;
    const available =
      contentWidth - other - gaps - MIN_SPACER_PX - COLLAPSE_EARLY_PX;
    setOverflowing(needed > available);
  }, [expand, actions.length, useModal]);

  useLayoutEffect(() => {
    checkOverflow();
    const root = rootRef.current;
    const toolbar = root?.closest("[data-listview-toolbar]");
    if (!(toolbar instanceof HTMLElement)) return;

    const ro = new ResizeObserver(() => checkOverflow());
    ro.observe(toolbar);
    if (measureRef.current) ro.observe(measureRef.current);
    window.addEventListener("resize", checkOverflow);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow, actions]);

  useLayoutEffect(() => {
    if (!expand || useModal) setMenuOpen(false);
  }, [expand, overflowing, useModal]);

  if (actions.length === 0 && !fullscreen) return null;

  const showExpanded =
    expand && !overflowing && !useModal && actions.length > 0;
  const showMenu = !showExpanded && actions.length > 0;

  const fsLabel = fullscreen?.isFullscreen
    ? t("shared.listView.exitFullscreen")
    : t("shared.listView.enterFullscreen");

  /** Index 0 sits closest to the right (nearest fullscreen / trailing edge). */
  const inlineActions = [...actions].reverse();

  const runAction = (actionId: string) => {
    actions.find((a) => a.id === actionId)?.onClick();
    setMenuOpen(false);
  };

  return (
    <div ref={rootRef} className={s.listViewToolbarActions}>
      {expand && actions.length > 0 && !useModal ? (
        <div
          ref={measureRef}
          className={s.listViewToolbarActionsMeasure}
          data-actions-measure=""
          aria-hidden
        >
          {inlineActions.map((action) => (
            <ActionButton key={`measure-${action.id}`} action={action} />
          ))}
        </div>
      ) : null}

      {showExpanded
        ? inlineActions.map((action) => (
            <ActionButton key={action.id} action={action} />
          ))
        : null}

      {showMenu ? (
        <>
          <Button
            ref={moreRef}
            type="button"
            size="sm"
            color="primary"
            variant="outline"
            className={s.listViewToolbarMoreBtn}
            iconLeft={<MoreHorizontal size={16} />}
            aria-label={t("shared.ui.moreActions")}
            aria-haspopup={useModal ? "dialog" : "menu"}
            aria-expanded={menuOpen}
            title={t("shared.ui.moreActions")}
            onClick={() => setMenuOpen((v) => !v)}
          />
          {useModal ? (
            <Modal
              open={menuOpen}
              setOpen={setMenuOpen}
              minWidth="320px"
              maxWidth="400px"
              minHeight="auto"
              container={getPortalContainer()}
            >
              <Modal.Header>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  List Actions
                </h3>
              </Modal.Header>
              <Modal.Body>
                <div className={s.actionsModalList}>
                  {actions.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className={s.actionsModalItem}
                      disabled={action.disabled}
                      onClick={() => runAction(action.id)}
                    >
                      {action.icon ? (
                        <span className={s.listViewHeaderMenuIcon}>
                          {action.icon}
                        </span>
                      ) : null}
                      {action.label}
                    </button>
                  ))}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  type="button"
                  color="primary"
                  variant="solid"
                  fullWidth
                  onClick={() => setMenuOpen(false)}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          ) : (
            <DropdownMenu
              anchorEl={moreRef}
              anchorPos="SE"
              openDirection="SW"
              open={menuOpen}
              setOpen={setMenuOpen}
              closeOnSelect
              onChange={(actionId) => {
                actions.find((a) => a.id === actionId)?.onClick();
              }}
            >
              {actions.map((action) => (
                <MenuItem
                  key={action.id}
                  value={action.id}
                  disabled={action.disabled}
                >
                  <span className={s.listViewHeaderMenuItem}>
                    {action.icon ? (
                      <span className={s.listViewHeaderMenuIcon}>
                        {action.icon}
                      </span>
                    ) : null}
                    {action.label}
                  </span>
                </MenuItem>
              ))}
            </DropdownMenu>
          )}
        </>
      ) : null}

      {fullscreen ? (
        <Button
          type="button"
          size="sm"
          color="primary"
          variant="outline"
          className={s.listViewToolbarIconAction}
          data-fullscreen-btn=""
          iconLeft={
            fullscreen.isFullscreen ? (
              <Minimize2 size={16} />
            ) : (
              <Maximize2 size={16} />
            )
          }
          title={fsLabel}
          aria-label={fsLabel}
          onClick={fullscreen.onToggle}
        />
      ) : null}
    </div>
  );
}
